import {hpcAttrs} from '@github-ui/hpc'
import {IconButton} from '@primer/react'
import {PencilIcon} from '@primer/octicons-react'
import {useRef, useState} from 'react'
import {DescriptionEditor} from './DescriptionEditor'
import {DescriptionEditHistory} from './DescriptionEditHistory'
import {useDescriptionData} from '../../page-data/loaders/use-description-data'
import {useOverviewRouteData} from './data/use-overview-route-data'
import type {SafeHTMLString} from '@github-ui/safe-html'
import styles from './Description.module.css'
import {ReactionViewerBase} from '@github-ui/reaction-viewer/ReactionViewerBase'
import type {ReactionContent} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import {useReactToDescriptionMutation} from '../../mutations/use-react-to-description-mutation'

import {useLayoutCanEditTitle} from '../../stores/layout-selectors'
import {applyVariablesToReactions} from '../../mutations/use-react-to-issue-comment-mutation'

/** PR description section on the Overview tab */
export function Description() {
  const {
    description: initialDescriptionHtml,
    descriptionText: initialDescriptionText,
    editInformation,
    reactionGroups: initialReactionGroups,
    viewerCanReact: initialViewerCanReact,
  } = useOverviewRouteData().data
  // Editing the title has the same permissions as editing the description
  const canEditDescription = useLayoutCanEditTitle()

  const {data: descriptionData} = useDescriptionData({
    initialBodyHtml: initialDescriptionHtml ?? undefined,
    initialBodyText: initialDescriptionText ?? undefined,
    initialEditInformation: editInformation,
    initialReactionGroups,
    initialViewerCanReact,
  })
  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string
  const description = descriptionData?.bodyHtml ? (descriptionData.bodyHtml as SafeHTMLString) : null
  const body = descriptionData?.body ?? ''

  const [isEditing, setIsEditing] = useState(false)
  const editButtonRef = useRef<HTMLButtonElement>(null)
  const descriptionRef = useRef<HTMLElement>(null)

  const reactionGroups = descriptionData?.reactionGroups ?? []
  const reactMutation = useReactToDescriptionMutation()
  const optimisticReactionGroups = reactMutation.isPending
    ? applyVariablesToReactions(reactionGroups, reactMutation.variables)
    : reactionGroups

  const onReact = (reaction: ReactionContent, viewerHasReacted: boolean) =>
    reactMutation.mutate({reaction, viewerHasReacted})

  return (
    <section ref={descriptionRef} tabIndex={-1} className={styles.container} aria-label="Description" {...hpcAttrs()}>
      <div className={styles.body}>
        <div className={styles.editMetadata} hidden={isEditing}>
          <DescriptionEditHistory returnFocusRef={descriptionRef} />
          {canEditDescription && (
            <IconButton
              ref={editButtonRef}
              icon={PencilIcon}
              aria-label="Edit description"
              variant="invisible"
              size="small"
              className={styles.editButton}
              onClick={() => setIsEditing(true)}
            />
          )}
        </div>
        <DescriptionEditor
          bodyHtml={description}
          body={body}
          isEditing={isEditing}
          onEditingChange={setIsEditing}
          returnFocusRef={editButtonRef}
        />
        {!isEditing && reactionGroups.length > 0 && (
          <div className={styles.reactions}>
            <ReactionViewerBase
              reactionContext="the description"
              reactionGroups={optimisticReactionGroups}
              canReact={descriptionData?.viewerCanReact}
              onReact={onReact}
            />
          </div>
        )}
      </div>
    </section>
  )
}
